import { useCallback, useMemo, useState } from "react";
import { updateFileCategory, uploadMatterDocument } from "../helpers/apiHelper";
import {
  applyClassification,
  isGroupedFilesResponse,
  missingMandatoryCategories,
  normalizeGroupedFiles,
} from "../helpers/documentCategories";
import {
  isGroupedFilesMockEnabled,
  mockUpdateFileCategory,
  rememberMockCategory,
  withGroupedFilesMock,
} from "../helpers/documentsMock";

/**
 * Loads a matter's documents, preferring the grouped-by-category shape (TDDS-3384).
 * `grouped` stays null when the backend only returns the flat `{ files }` list, so pages keep
 * their original flat rendering. `load` must return the raw files response.
 */
const useCategorizedDocuments = ({ load, matterId, token, author, mockCanEdit = true }) => {
  const [files, setFiles] = useState([]);
  const [grouped, setGrouped] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [focusRequest, setFocusRequest] = useState(null);

  const reload = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setFailed(false);
    try {
      const response = withGroupedFilesMock(await load(), matterId, { canEdit: mockCanEdit });
      if (isGroupedFilesResponse(response)) {
        setGrouped(normalizeGroupedFiles(response));
        setFiles([]);
      } else {
        setGrouped(null);
        setFiles(response?.files || []);
      }
    } catch (error) {
      console.error("Failed to fetch documents:", error);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [load, matterId, mockCanEdit]);

  const reclassify = useCallback(async (doc, category) => {
    const updated = isGroupedFilesMockEnabled()
      ? await mockUpdateFileCategory(matterId, doc, category)
      : await updateFileCategory(doc.id, category, token);
    const next = { ...doc, ...(updated || {}), category: updated?.category ?? category };
    setGrouped((current) => (current ? applyClassification(current, next) : current));
    return next;
  }, [matterId, token]);

  const upload = useCallback(async (file, category) => {
    const result = await uploadMatterDocument({ token, file, matterId, author, category });
    if (isGroupedFilesMockEnabled()) {
      rememberMockCategory(matterId, result?.fileID || result?.fileId || result?.id, category);
    }
    await reload({ silent: true });
  }, [author, matterId, reload, token]);

  const missing = useMemo(() => missingMandatoryCategories(grouped), [grouped]);
  const focusCategory = useCallback((key) => setFocusRequest({ key, nonce: Date.now() }), []);

  return { files, grouped, loading, failed, reload, reclassify, upload, missing, focusRequest, focusCategory };
};

export default useCategorizedDocuments;
